import type React from "react";
import {observer} from "mobx-react-lite";
import {Button, Stack, Typography} from "@mui/material";
import {authStore, notificationStore} from "~/stores/init";
import type {SupplierSuggestion} from "~/pages/supplier-360/types/Supplier360PdlTypes";
import {useDecideSuggestionMutation} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useDecideSuggestionMutation";

/**
 * "Suggested domain: biffa.co.uk · Use · Dismiss" under an empty Domain field. The backend only
 * offers this suggestion when the entity has no domain and none was set by hand; "Use" saves it as
 * a manual edit. Read-only users see the suggestion without the buttons — the API refuses their
 * decision too.
 */
export const DomainSuggestion: React.FC<{suggestion: SupplierSuggestion}> = observer(({suggestion}) => {
    const {mutate, isPending} = useDecideSuggestionMutation();
    const canEdit = authStore.hasEditPermission;

    const decide = (decision: 'accept' | 'dismiss') => mutate({suggestion, decision}, {
        onSuccess: () => notificationStore.show(
            decision === 'accept' ? `Domain set to ${suggestion.value}.` : 'Suggestion dismissed.',
            {severity: 'success', id: `supplier-suggestion-${suggestion.id}`, autoHideDuration: 3000},
        ),
        onError: () => notificationStore.show('Could not save that decision. Please try again.', {
            severity: 'error', id: `supplier-suggestion-error-${suggestion.id}`, autoHideDuration: 3000,
        }),
    });

    return (
        <Stack gap={0.25} sx={{minWidth: 0}}>
            <Typography variant="caption" color="text.secondary">Suggested domain</Typography>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{minWidth: 0}}>
                <Typography variant="body2" fontWeight={600} noWrap title={suggestion.value} sx={{minWidth: 0}}>
                    {suggestion.value}
                </Typography>
                {canEdit && (
                    <Stack direction="row" sx={{flexShrink: 0}}>
                        <Button size="small" disabled={isPending} onClick={() => decide('accept')} sx={{minWidth: 0, py: 0, px: 0.75}}>
                            Use
                        </Button>
                        <Button size="small" color="inherit" disabled={isPending} onClick={() => decide('dismiss')}
                                sx={{minWidth: 0, py: 0, px: 0.75}}>
                            Dismiss
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
});
